/* Pink & Blue Co. — Product Card Carousel
   No autoplay, polling, or background animation loop.
*/

(() => {
  if (customElements.get("pb-card-carousel")) return;

  class PBCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;

      this.track = this.querySelector(".pb-card-carousel__track");
      if (!this.track) return;

      this.slides = [...this.track.children];
      this.dots = [...this.querySelectorAll("[data-pb-index]")];
      this.previous = this.querySelector("[data-pb-previous]");
      this.next = this.querySelector("[data-pb-next]");
      this.index = 0;
      this.frame = 0;

      this.controller = new AbortController();

      const options = {
        signal: this.controller.signal
      };

      this.setAttribute("data-ready", "");
      this.track.scrollTo({
        left: 0,
        behavior: "instant"
      });

      if (this.slides.length < 2) return;

      this.querySelector(".pb-card-carousel__controls").hidden = false;
      this.update(0);

      this.track.addEventListener(
        "pointerdown",
        (event) => {
          this.startX = event.clientX;
          this.startY = event.clientY;
          this.dragged = false;

          this.prepare();

          event.stopPropagation();
        },
        options
      );

      this.track.addEventListener(
        "pointermove",
        (event) => {
          if (this.startX == null) return;

          const dx = Math.abs(event.clientX - this.startX);
          const dy = Math.abs(event.clientY - this.startY);

          if (dx > 8 && dx > dy) {
            this.dragged = true;
          }
        },
        {
          ...options,
          passive: true
        }
      );

      this.track.addEventListener(
        "pointercancel",
        () => {
          this.startX = null;
          this.startY = null;
        },
        options
      );

      this.track.addEventListener(
        "click",
        (event) => {
          if (this.dragged) {
            event.preventDefault();
            event.stopPropagation();
          }

          this.startX = null;
          this.startY = null;
          this.dragged = false;
        },
        options
      );

      this.track.addEventListener(
        "scroll",
        () => {
          if (this.frame) return;

          this.frame = requestAnimationFrame(() => {
            this.frame = 0;

            const width = this.track.clientWidth;
            if (!width) return;

            const index = Math.max(
              0,
              Math.min(
                this.slides.length - 1,
                Math.round(this.track.scrollLeft / width)
              )
            );

            this.load(index);
            this.load(index - 1);
            this.load(index + 1);
            this.update(index);
          });
        },
        {
          ...options,
          passive: true
        }
      );

      this.addEventListener(
        "click",
        (event) => {
          const button = event.target.closest("button");

          if (!button || !this.contains(button)) return;

          event.stopPropagation();

          if (button.hasAttribute("data-pb-index")) {
            this.go(Number(button.dataset.pbIndex));
          } else if (button === this.previous) {
            this.go(this.index - 1);
          } else if (button === this.next) {
            this.go(this.index + 1);
          }
        },
        options
      );

      this.track.addEventListener(
        "keydown",
        (event) => {
          if (
            event.key !== "ArrowLeft" &&
            event.key !== "ArrowRight"
          ) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          this.go(
            this.index + (event.key === "ArrowRight" ? 1 : -1)
          );
        },
        options
      );

      this.card = this.closest("product-card");

      // Return to the primary image when a color swatch changes.
      this.card?.addEventListener(
        "change",
        (event) => {
          if (!event.target.closest("fieldset")) return;

          this.track.scrollTo({
            left: 0,
            behavior: "instant"
          });

          this.update(0);
        },
        options
      );
    }

    load(index) {
      const template =
        this.slides[index]?.querySelector("template");

      if (template) {
        template.replaceWith(
          template.content.cloneNode(true)
        );
      }
    }

    prepare() {
      this.load(this.index - 1);
      this.load(this.index + 1);

      if (
        !this.observer &&
        typeof ResizeObserver !== "undefined"
      ) {
        this.lastWidth = this.track.clientWidth;

        this.observer = new ResizeObserver(() => {
          const width = this.track.clientWidth;

          if (!width || width === this.lastWidth) return;

          this.lastWidth = width;

          this.track.scrollTo({
            left: this.index * width,
            behavior: "instant"
          });
        });

        this.observer.observe(this.track);
      }
    }

    go(index) {
      index = Math.max(
        0,
        Math.min(this.slides.length - 1, index)
      );

      this.prepare();

      // Load images along the path of a dot jump.
      for (
        let i = Math.min(this.index, index);
        i <= Math.max(this.index, index);
        i++
      ) {
        this.load(i);
      }

      const reducedMotion = matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.track.scrollTo({
        left: index * this.track.clientWidth,
        behavior: reducedMotion ? "instant" : "smooth"
      });
    }

    update(index) {
      this.index = index;

      this.dots.forEach((dot, i) => {
        dot.setAttribute(
          "aria-current",
          String(i === index)
        );
      });

      this.slides.forEach((slide, i) => {
        slide.tabIndex = i === index ? 0 : -1;
      });

      if (this.previous) {
        this.previous.disabled = index === 0;
      }

      if (this.next) {
        this.next.disabled =
          index === this.slides.length - 1;
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;

      this.observer?.disconnect();
      this.observer = null;

      if (this.frame) {
        cancelAnimationFrame(this.frame);
      }

      this.frame = 0;
    }
  }

  customElements.define(
    "pb-card-carousel",
    PBCardCarousel
  );
})();